import {useCurrentWorkspaceUrlPrefix, useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {useCancelOrganizationDeletion, useGetOrganizationById} from '@tinct/core-api';
import {BarChartCardDataPoint} from '@tinct/web-components';
import {toast} from 'sonner';
import {LayoutTopbar} from '@tinct/web-layout';
// import {GameModal} from '@tinct/web-layout'; // TEMP_GAME_TEST_BUTTON
// import {Button} from '@tinct/web-components'; // TEMP_GAME_TEST_BUTTON
// import {useState} from 'react'; // TEMP_GAME_TEST_BUTTON
import {useConnectedUser} from '@tinct/core-store-session';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {DashboardHeader} from './components/dashboard-header';
import {DeletionAlert} from './components/deletion-alert';
import {WelcomeBanner} from './components/welcome-banner';
import {DashboardMetrics} from './components/dashboard-metrics';
import {DashboardCharts} from './components/dashboard-charts';
import {CampaignsList} from './components/campaigns-list';

export function DashboardPage() {
  const workspaceId = useGetCurrentWorkspaceId();
  const workspaceBaseUrl = useCurrentWorkspaceUrlPrefix();
  const connectedUser = useConnectedUser();
  const assetsUrl = useStaticAssetsUrl();
  // const [gameOpen, setGameOpen] = useState(false); // TEMP_GAME_TEST_BUTTON

  const cancelDeletion = useCancelOrganizationDeletion({
    onSuccess: () => toast.success('Workspace deletion cancelled'),
    onError: () => toast.error('Failed to cancel deletion'),
  });

  const {data: organization, isLoading} = useGetOrganizationById(
    {
      id: workspaceId ?? '',
      fields: {
        deleteAt: null,
        lastBrandVersion: {
          fields: {
            id: null,
            version: null,
            name: null,
            valueAndMessaging: null,
          }
        },
        campaigns: {
          fields: {
            name: null,
            id: null,
            status: null,
            statistics: {
              fields: {
                totalPagesCount: null,
                visitsCount: null,
              },
            },
          },
        },
        statistics: {
          fields: {
            audiencesCount: null,
            leadsCount: null,
            pageVariantsCount: null,
            publishedPageVariantsCount: null,
            visitsCount: null,
            visitsVariation: null,
            visitsTimeSeries: null,
            totalCampaignsCount: null,
            publishedCampaignsCount: null,
          }
        }
      }
    },
    {
      enabled: !!workspaceId,
    }
  );

  const statistics = organization?.statistics;

  const chartData: BarChartCardDataPoint[] =
    statistics?.visitsTimeSeries?.series?.map((point) => ({
      name: point.name ?? '',
      date: point.date ?? new Date(),
      value: point.value ?? 0,
    })) ?? [];

  const publishedPercentage =
    statistics?.pageVariantsCount && statistics?.publishedPageVariantsCount
      ? Math.round(
        (statistics.publishedPageVariantsCount / statistics.pageVariantsCount) * 100
      )
      : 0;

  const hasAnyVisits = statistics?.visitsTimeSeries?.series?.some((point) => (point.value ?? 0) > 0);

  return (
    <div>
      <header className="sticky top-0 z-30">
        <LayoutTopbar/>
      </header>
      <div>
        <div className="flex flex-col gap-12 px-8 pt-8 pb-12 max-w-7xl mx-auto">
          {/* TEMP_GAME_TEST_BUTTON
          <Button onClick={() => setGameOpen(true)} className="self-start">
            Play game (test)
          </Button>
          <GameModal open={gameOpen} onOpenChange={setGameOpen} />
          TEMP_GAME_TEST_BUTTON */}
          <DashboardHeader workspaceBaseUrl={workspaceBaseUrl} lastBrandVersion={organization?.lastBrandVersion}/>

          {organization?.deleteAt && (
            <DeletionAlert
              deleteAt={organization.deleteAt}
              isPending={cancelDeletion.isPending}
              onCancelDeletion={() => cancelDeletion.mutate({organizationId: workspaceId!})}
            />
          )}

          {!organization?.campaigns?.length && (
            <WelcomeBanner
              firstName={connectedUser?.firstName}
              lastBrandVersion={organization?.lastBrandVersion}
              workspaceBaseUrl={workspaceBaseUrl}
              assetsUrl={assetsUrl}
            />
          )}

          <DashboardMetrics
            assetsUrl={assetsUrl}
            totalCampaignsCount={statistics?.totalCampaignsCount ?? 0}
            publishedCampaignsCount={statistics?.publishedCampaignsCount ?? 0}
            pageVariantsCount={statistics?.pageVariantsCount ?? 0}
            publishedPercentage={publishedPercentage}
            visitsCount={statistics?.visitsCount ?? 0}
            visitsVariation={statistics?.visitsVariation}
          />

          <DashboardCharts
            chartData={chartData}
            hasAnyVisits={!!hasAnyVisits}
            isLoading={isLoading}
          />

          {!!organization?.campaigns?.length && (
            <CampaignsList campaigns={organization.campaigns}/>
          )}
        </div>
      </div>
    </div>
  );
}
